import { useState } from 'react';

export interface ApprovalTarget {
  /** The permission the action needs, e.g. <c>store.orders.void_line</c>. */
  actionCode: string;
  targetType: string;
  targetId: string;
  /** What is being approved, already translated, e.g. "Void Phở bò tái" (FR-AUTH-08). */
  summary: string;
}

export interface PendingApproval {
  target: ApprovalTarget;
  retry(approvalId: string): Promise<void>;
  cancel(): void;
}

/** The error shape API clients throw: RFC 9457 problem with the business `code`. */
interface Problem {
  code?: string;
  status?: number;
}

const APPROVAL_CODES = new Set([
  'ordering.approval_required',
  'payments.approval_required',
  'shifts.approval_required',
  'shifts.drawer_approval_required',
]);
export const APPROVAL_CANCELLED = 'approval.cancelled';

/**
 * Runs an action; if the server answers that a manager must approve, <ApprovalPrompt> asks for the manager's staff
 * code and password, gets a single-use approval for exactly this action and record, and the action runs again with
 * it (FR-AUTH-07). Shared by the POS and the admin portals.
 */
export function useApprovalFlow() {
  const [pending, setPending] = useState<PendingApproval>();

  const run = async <T>(target: ApprovalTarget, action: (approvalId: string | null) => Promise<T>): Promise<T> => {
    try {
      return await action(null);
    } catch (error) {
      if (!APPROVAL_CODES.has((error as Problem).code ?? '')) throw error;
      return new Promise<T>((resolve, reject) => {
        setPending({
          target,
          retry: async (approvalId) => {
            resolve(await action(approvalId));
            setPending(undefined);
          },
          cancel: () => {
            reject({ code: APPROVAL_CANCELLED, status: 0 } satisfies Problem);
            setPending(undefined);
          },
        });
      });
    }
  };

  return { run, pending };
}

/** A cancelled approval is not an error to show. */
export const isApprovalCancelled = (error: unknown) => (error as Problem | null)?.code === APPROVAL_CANCELLED;
